/* Bol Invoice Requests Tab — fatura talep eden Bol siparislerini listele,
 * tek tikla / toplu olarak Billit'e gonder, gecmisi gor.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    FileText, RefreshCw, Loader2, Send, CheckCircle2, AlertCircle, Mail,
    MapPin, Briefcase, Euro, Package,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const fmtEur = (n) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(n || 0);

const STATUS_LABEL = {
    OPEN: { txt: 'Açık', cls: 'bg-amber-100 text-amber-800' },
    INVOICE_REQUESTED: { txt: 'Talep edildi', cls: 'bg-amber-100 text-amber-800' },
    INVOICE_UPLOADED: { txt: 'Yüklendi', cls: 'bg-cyan-100 text-cyan-700' },
    FINISHED: { txt: 'Tamamlandı', cls: 'bg-emerald-100 text-emerald-700' },
};

export default function BolInvoiceRequests() {
    const [items, setItems] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [pushingIds, setPushingIds] = useState(new Set());
    const [bulkPushing, setBulkPushing] = useState(false);
    const [tab, setTab] = useState('pending'); // pending | done | all
    const [selected, setSelected] = useState(new Set());

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await axios.get(`${API}/api/bol/invoice-requests`, {
                headers: hdrs(),
                params: { status_filter: tab === 'all' ? 'all' : tab },
            });
            setItems(r.data?.items || []);
            setSummary(r.data?.summary || null);
            setSelected(new Set());
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Liste yüklenemedi');
        } finally { setLoading(false); }
    }, [tab]);

    useEffect(() => { load(); }, [load]);

    const syncNow = async () => {
        setSyncing(true);
        try {
            const r = await axios.post(`${API}/api/bol/invoice-requests/sync`, {}, { headers: hdrs() });
            toast.success(`${r.data.fetched} fatura talebi sync edildi (${r.data.orders_flagged} sipariş işaretlendi)`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Sync hatası');
        } finally { setSyncing(false); }
    };

    const pushOne = async (orderId) => {
        setPushingIds((prev) => new Set([...prev, orderId]));
        try {
            const r = await axios.post(`${API}/api/bol/orders/${orderId}/push-billit`, {}, { headers: hdrs() });
            if (r.data?.already_pushed) {
                toast.info('Bu sipariş zaten Billit\'e gönderilmiş');
            } else {
                toast.success(`Billit'e gönderildi: ${r.data?.billit_order_id || 'OK'}`);
            }
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Billit push hatası');
        } finally {
            setPushingIds((prev) => {
                const n = new Set(prev);
                n.delete(orderId);
                return n;
            });
        }
    };

    const pushSelected = async () => {
        if (!selected.size) { toast.error('Önce sipariş seçin'); return; }
        if (selected.size > 50) { toast.error('Tek seferde en fazla 50'); return; }
        setBulkPushing(true);
        try {
            const r = await axios.post(`${API}/api/bol/invoice-requests/push-bulk`,
                { order_ids: Array.from(selected) }, { headers: hdrs() });
            toast.success(`${r.data.succeeded}/${r.data.total} başarılı (${r.data.failed} hata)`);
            if (r.data.failed > 0) {
                const errs = r.data.results.filter((x) => !x.ok).slice(0, 3)
                    .map((x) => `${x.order_id}: ${x.error}`).join(', ');
                toast.error(`Bazı hatalar: ${errs}`);
            }
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Toplu push hatası');
        } finally { setBulkPushing(false); }
    };

    const toggleAll = () => {
        const pushable = items.filter((i) => !i.billit_pushed);
        if (selected.size === pushable.length) setSelected(new Set());
        else setSelected(new Set(pushable.map((i) => i.order_id)));
    };

    const toggleOne = (oid) => {
        const next = new Set(selected);
        if (next.has(oid)) next.delete(oid);
        else next.add(oid);
        setSelected(next);
    };

    const pushableCount = items.filter((i) => !i.billit_pushed).length;

    return (
        <div className="space-y-4" data-testid="bol-invoice-requests">
            {/* Header + Sync */}
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                    <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
                        <FileText size={18} /> Fatura Talepleri
                    </h2>
                    <p className="text-xs text-gray-500 mt-0.5">
                        Müşterilerin Bol&apos;da &quot;Faktuur opvragen&quot; ile talep ettiği faturalar.
                    </p>
                </div>
                <button onClick={syncNow} disabled={syncing}
                    className="h-9 px-3 text-xs font-bold text-white bg-gray-900 hover:bg-black disabled:opacity-50 rounded-md flex items-center gap-1.5"
                    data-testid="bir-sync">
                    {syncing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                    Bol&apos;dan Sync
                </button>
            </div>

            {/* Summary cards */}
            {summary && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <SCard label="Toplam Talep" value={summary.total} icon={FileText} color="blue" />
                    <SCard label="Bekleyen" value={summary.pending} icon={AlertCircle} color="amber" />
                    <SCard label="Tamamlanan" value={summary.done} icon={CheckCircle2} color="emerald" />
                    <SCard label="Billit'e Gönd." value={summary.billit_pushed} icon={Send} color="cyan" />
                    <SCard label="B2B" value={summary.b2b} icon={Briefcase} color="indigo" />
                </div>
            )}

            {/* Sub-tabs */}
            <div className="inline-flex rounded-md border border-gray-300 overflow-hidden bg-white" data-testid="bir-tabs">
                {[
                    { k: 'pending', label: 'Bekleyen' },
                    { k: 'done', label: 'Geçmiş' },
                    { k: 'all', label: 'Hepsi' },
                ].map((t) => (
                    <button key={t.k} onClick={() => setTab(t.k)}
                        className={`h-9 px-4 text-xs font-bold border-l border-gray-300 first:border-l-0 ${
                            tab === t.k ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-50'
                        }`}
                        data-testid={`bir-tab-${t.k}`}>
                        {t.label}
                    </button>
                ))}
            </div>

            {/* Bulk action bar */}
            {pushableCount > 0 && (
                <div className="flex items-center justify-between gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                    <div className="text-xs text-blue-900">
                        <strong>{selected.size}</strong> seçildi · <strong>{pushableCount}</strong> push edilebilir
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={toggleAll}
                            className="h-8 px-2 text-xs font-semibold text-blue-700 bg-white hover:bg-blue-50 border border-blue-200 rounded"
                            data-testid="bir-select-all">
                            {selected.size === pushableCount && selected.size > 0 ? 'Seçimi Kaldır' : 'Hepsini Seç'}
                        </button>
                        <button onClick={pushSelected} disabled={!selected.size || bulkPushing}
                            className="h-8 px-3 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded flex items-center gap-1.5"
                            data-testid="bir-bulk-push">
                            {bulkPushing ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                            Seçilenleri Billit'e Gönder ({selected.size})
                        </button>
                    </div>
                </div>
            )}

            {/* Table */}
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="w-full text-xs">
                    <thead className="bg-gray-50 text-gray-600 uppercase">
                        <tr>
                            <th className="p-2.5 w-10"></th>
                            <th className="text-left p-2.5">Sipariş</th>
                            <th className="text-left p-2.5">Müşteri</th>
                            <th className="text-left p-2.5">BTW</th>
                            <th className="text-left p-2.5">Lokasyon</th>
                            <th className="text-right p-2.5">Tutar</th>
                            <th className="text-left p-2.5">Bol Durumu</th>
                            <th className="text-left p-2.5">Billit</th>
                            <th className="text-right p-2.5">İşlem</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((r) => {
                            const sb = STATUS_LABEL[r.status] || { txt: r.status, cls: 'bg-gray-100 text-gray-600' };
                            const checked = selected.has(r.order_id);
                            const canSelect = !r.billit_pushed;
                            return (
                                <tr key={r.order_id} className={`border-t border-gray-100 hover:bg-blue-50/20 ${checked ? 'bg-blue-50/50' : ''}`}
                                    data-testid={`bir-row-${r.order_id}`}>
                                    <td className="p-2.5 text-center">
                                        {canSelect && (
                                            <input type="checkbox" checked={checked} onChange={() => toggleOne(r.order_id)}
                                                className="h-4 w-4 accent-blue-600 cursor-pointer"
                                                data-testid={`bir-select-${r.order_id}`} />
                                        )}
                                    </td>
                                    <td className="p-2.5">
                                        <div className="font-mono font-bold text-gray-900">{r.order_id}</div>
                                        <div className="text-[10px] text-gray-500">{r.order_date || '—'}</div>
                                    </td>
                                    <td className="p-2.5">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                            <span className="font-semibold text-gray-900">{r.customer_name}</span>
                                            {r.is_b2b && (
                                                <span className="px-1.5 py-0.5 text-[9px] font-bold bg-indigo-100 text-indigo-800 rounded inline-flex items-center gap-0.5">
                                                    <Briefcase size={9} /> B2B
                                                </span>
                                            )}
                                        </div>
                                        {r.company && (
                                            <div className="text-[10px] text-gray-600 truncate max-w-[180px]" title={r.company}>{r.company}</div>
                                        )}
                                    </td>
                                    <td className="p-2.5">
                                        {r.vat_number ? (
                                            <span className="font-mono text-[11px] text-gray-900">{r.vat_number}</span>
                                        ) : (
                                            <span className="text-gray-300">—</span>
                                        )}
                                    </td>
                                    <td className="p-2.5 text-gray-600">
                                        <div className="flex items-center gap-1">
                                            <MapPin size={10} />
                                            {[r.city, r.country].filter(Boolean).join(', ') || '—'}
                                        </div>
                                    </td>
                                    <td className="p-2.5 text-right font-semibold text-gray-900">{fmtEur(r.total_incl_vat)}</td>
                                    <td className="p-2.5">
                                        <span className={`px-2 py-0.5 text-[10px] font-bold rounded ${sb.cls}`}>{sb.txt}</span>
                                    </td>
                                    <td className="p-2.5">
                                        {r.billit_pushed ? (
                                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold bg-cyan-100 text-cyan-700 rounded">
                                                <CheckCircle2 size={10} /> {r.billit_order_id || 'OK'}
                                            </span>
                                        ) : (
                                            <span className="text-gray-400 text-[10px]">—</span>
                                        )}
                                    </td>
                                    <td className="p-2.5 text-right">
                                        {!r.billit_pushed && (
                                            <button onClick={() => pushOne(r.order_id)}
                                                disabled={pushingIds.has(r.order_id) || bulkPushing}
                                                className="h-7 px-2 text-[11px] font-bold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded inline-flex items-center gap-1"
                                                data-testid={`bir-push-${r.order_id}`}>
                                                {pushingIds.has(r.order_id) ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
                                                Billit
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {!items.length && !loading && (
                    <div className="text-center py-10 text-sm text-gray-500">
                        {tab === 'pending'
                            ? 'Bekleyen fatura talebi yok. Bol\'dan sync edip yeni talepleri kontrol edin.'
                            : tab === 'done'
                                ? 'Henüz tamamlanmış talep yok.'
                                : 'Hiç fatura talebi yok. Önce "Bol\'dan Sync" yapın.'}
                    </div>
                )}
                {loading && (
                    <div className="text-center py-6">
                        <Loader2 size={20} className="animate-spin text-gray-400 inline-block" />
                    </div>
                )}
            </div>
        </div>
    );
}

function SCard({ label, value, icon: Icon, color }) {
    const cls = {
        blue: 'bg-blue-50 text-blue-700',
        amber: 'bg-amber-50 text-amber-700',
        emerald: 'bg-emerald-50 text-emerald-700',
        cyan: 'bg-cyan-50 text-cyan-700',
        indigo: 'bg-indigo-50 text-indigo-700',
    };
    return (
        <div className="bg-white border border-gray-200 rounded-lg p-3">
            <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">{label}</span>
                <div className={`w-7 h-7 rounded-md flex items-center justify-center ${cls[color]}`}><Icon size={14} /></div>
            </div>
            <div className="text-xl font-black text-gray-900">{value}</div>
        </div>
    );
}
